import React from 'react';
import {
  TableBody,
  TableCell,
  TableRow,
  Table,
  TableHeader,
  TableHeaderCell,
  TableSelectionCell,
  DataGridProps,
  useArrowNavigationGroup,
  useTableFeatures,
  useTableSelection,
  useTableSort,
  mergeClasses,
  TableRowProps,
} from '@fluentui/react-components';

import { AggregationMode, I18n } from '../types';
import { useRenderingData } from '../hooks/use-rendering-props';
import { useTableViewStyles } from './table-view-styles';

interface TableViewProps<T> {
  additionalDataGridHeader?: any;
  additionalDataGridProps?: DataGridProps;
  aggregateMode?: AggregationMode;
  classes?: any;
  columns: DataGridProps['columns'];
  customFilter?: (item: T, search: string) => boolean;
  disableClientSort?: boolean;
  disabledToggleWholeRow?: boolean;
  i18n?: I18n;
  infiniteScroll?: boolean;
  selectionMode: (DataGridProps['selectionMode'] & boolean);
  appearanceRow: TableRowProps['appearance'];
  showDefaultSelectionHeader?: boolean;
  showEmptyGridResult?: boolean;
  showSelectedOnly?: boolean;
  showClearAllHeader?: boolean;
}

const isColumnSortable = (column) => column.compare.length > 0;

export const TableGridView: React.FC<TableViewProps<any>> = ({
  i18n,
  classes = {},
  selectionMode,
  aggregateMode,
  showSelectedOnly,
  customFilter,
  additionalDataGridProps,
  showClearAllHeader,
  showDefaultSelectionHeader,
  showEmptyGridResult,
  disabledToggleWholeRow,
  disableClientSort,
  appearanceRow,
  columns,
}) => {
  const defaultClasses = useTableViewStyles();
  const {
    items, onChange, dataSelection,
  } = useRenderingData({
    mode: aggregateMode,
    showSelectedOnly,
    customFilter,
  });
  const { sortState, onSortChange } = additionalDataGridProps || {};
  const getRowId = (item) => item.value;
  const {
    getRows,
    selection: {
      allRowsSelected,
      someRowsSelected,
      toggleAllRows,
      toggleRow,
      isRowSelected,
      clearRows,
    },
    sort: { getSortDirection, toggleColumnSort, sort },
  } = useTableFeatures(
    {
      columns,
      items,
      getRowId,
    },
    [
      useTableSelection({
        selectionMode,
        selectedItems: dataSelection.map((item) => item.value),
        onSelectionChange: (e, data) => onChange(data.selectedItems),
      }),
      useTableSort({
        sortState,
        onSortChange,
      }),
    ],
  );

  const rowsInternal = getRows((row) => {
    const selected = isRowSelected(row.rowId);
    return {
      ...row,
      onClick: (e: React.MouseEvent) => {
        toggleRow(e, row.rowId);
      },
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === ' ') {
          e.preventDefault();
          toggleRow(e, row.rowId);
        }
      },
      selected,
      appearance: selected ? (appearanceRow || ('brand' as const)) : ('none' as const),
    };
  });

  const rows = disableClientSort ? rowsInternal : sort(rowsInternal);

  const headerSortProps = (columnId) => ({
    onClick: (e: React.MouseEvent) => {
      toggleColumnSort(e, columnId);
    },
    sortDirection: getSortDirection(columnId),
  });

  const keyboardNavAttr = useArrowNavigationGroup({ axis: 'grid' });

  const clearAll = (ev) => {
    clearRows(ev);
  };

  if (!items.length && !showEmptyGridResult) { return null; }

  return (
    <Table
      {...keyboardNavAttr}
      role="grid"
      className={classes.dataGrid}
      {...additionalDataGridProps}
    >
      <TableHeader>
        <TableRow>
          {showClearAllHeader && selectionMode && (
          <TableHeaderCell onClick={clearAll} className={mergeClasses(defaultClasses.clearAll, classes.clearAllBtn)}>
            <div className="clear-all-btn" role="button" aria-label={i18n.getString(_TL_('Clear all'))}>
              {`${i18n.getString(_TL_('Clear all'))} (${dataSelection.length})`}
            </div>
          </TableHeaderCell>
          )}
          {showDefaultSelectionHeader && selectionMode && (
          <TableSelectionCell
            checked={
            // eslint-disable-next-line no-nested-ternary
            allRowsSelected ? true : someRowsSelected ? 'mixed' : false
          }
            aria-checked={
            // eslint-disable-next-line no-nested-ternary
            allRowsSelected ? true : someRowsSelected ? 'mixed' : false
          }
            role="checkbox"
            onClick={toggleAllRows}
            checkboxIndicator={{ 'aria-label': i18n.getString(_TL_('Select all rows')) }}
          />
          ) }

          {
            columns.map((definitionColumn) => <TableHeaderCell key={definitionColumn.columnId} {...(isColumnSortable(definitionColumn) && headerSortProps(definitionColumn.columnId))}>{definitionColumn.renderHeaderCell()}</TableHeaderCell>)
          }

        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map(({
          item, selected, onClick, onKeyDown, appearance,
        }) => (
          <TableRow
            key={item.value}
            onClick={disabledToggleWholeRow ? undefined : onClick}
            onKeyDown={onKeyDown}
            aria-selected={selected}
            appearance={appearance}
          >
            {selectionMode && (
            <TableSelectionCell
              role="gridcell"
              aria-selected={selected}
              checked={selected}
              onClick={disabledToggleWholeRow ? onClick : undefined}
              checkboxIndicator={{ 'aria-label': i18n.getString(_TL_('Select rows')) }}
            />
            )}
            {
              columns.map((definitionColumn) => <TableCell key={definitionColumn.columnId}>{definitionColumn.renderCell(item)}</TableCell>)
            }
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
